/**
 * The guides.
 *
 * Markdown files with a small frontmatter block, so writing one needs no React and no route
 * registration — drop in a `.md` and it appears in the sidebar.
 *
 * Content is loaded **per language, on demand**. An earlier version globbed every language eagerly
 * into the main bundle, which works for two languages and quietly stops working at five: every
 * reader downloads every translation to read one. Each language is now its own chunk
 * (`content/<code>/index.ts`), fetched when it is actually asked for.
 */
import { AVAILABLE_LANGS, loadLanguage } from '@/content';

export interface Doc {
  slug: string;
  title: string;
  section: string;
  order: number;
  summary: string;
  body: string;
}

export const DEFAULT_LANG = 'en';
export { AVAILABLE_LANGS };

/**
 * Which language to show.
 *
 * The apps share an `edlang` cookie on the parent domain so language follows a reader across the
 * separate SPAs; `?lang` overrides it for a single visit. English is the fallback because it is the
 * only language every page is guaranteed to exist in.
 */
export function currentLang(): string {
  const param = new URLSearchParams(window.location.search).get('lang');
  const cookie = /(?:^|;\s*)edlang=([^;]+)/.exec(document.cookie)?.[1];
  const nav = navigator.language || DEFAULT_LANG;
  for (const candidate of [param, cookie, nav]) {
    const code = candidate?.slice(0, 2).toLowerCase();
    if (code && AVAILABLE_LANGS.includes(code)) return code;
  }
  return DEFAULT_LANG;
}

/**
 * A deliberately small frontmatter reader — `key: value` lines between `---` fences.
 *
 * A YAML dependency would buy nested structures nobody writing a help page needs, and every
 * dependency added to the workspace is one more thing that can break `npm ci`, which all eight
 * image builds run.
 */
function parse(path: string, text: string): Doc {
  const slug = path.split('/').pop()!.replace(/\.md$/, '');
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(text);
  const meta: Record<string, string> = {};

  if (match) {
    for (const line of match[1].split(/\r?\n/)) {
      const at = line.indexOf(':');
      if (at > 0) meta[line.slice(0, at).trim()] = line.slice(at + 1).trim();
    }
  }

  return {
    slug,
    title: meta.title || slug,
    section: meta.section || 'Guides',
    order: Number(meta.order ?? 999),
    summary: meta.summary || '',
    body: match ? text.slice(match[0].length) : text,
  };
}

const toDocs = (files: Record<string, string>): Doc[] =>
  Object.entries(files).map(([path, text]) => parse(path, text));

export interface DocSet {
  lang: string;
  docs: Doc[];
  sections: { name: string; docs: Doc[] }[];
  /** Slugs shown in English because the reader's language has no page for them. */
  fallbacks: Set<string>;
}

/**
 * Load one language, with English underneath it.
 *
 * A partly-translated set reads as a complete manual with some English pages, rather than a menu
 * that shrinks when you switch language — which would make missing translations look like missing
 * features.
 */
export async function loadDocs(lang: string): Promise<DocSet> {
  const [preferredFiles, fallbackFiles] = await Promise.all([
    loadLanguage(lang),
    lang === DEFAULT_LANG ? Promise.resolve(null) : loadLanguage(DEFAULT_LANG),
  ]);

  const preferred = preferredFiles ? toDocs(preferredFiles) : [];
  const fallback = fallbackFiles ? toDocs(fallbackFiles) : [];

  const bySlug = new Map<string, Doc>();
  const fallbacks = new Set<string>();

  for (const doc of fallback) {
    bySlug.set(doc.slug, doc);
    fallbacks.add(doc.slug);
  }
  for (const doc of preferred) {
    bySlug.set(doc.slug, doc);
    fallbacks.delete(doc.slug);
  }

  const docs = [...bySlug.values()].sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title),
  );

  const sections = docs.reduce<{ name: string; docs: Doc[] }[]>((acc, doc) => {
    const existing = acc.find((s) => s.name === doc.section);
    if (existing) existing.docs.push(doc);
    else acc.push({ name: doc.section, docs: [doc] });
    return acc;
  }, []);

  return { lang, docs, sections, fallbacks };
}

/**
 * Search over title, summary and body.
 *
 * Body matches carry a snippet so a hit in the middle of a long page is explicable — "why did this
 * result appear?" is the question a title-only search cannot answer.
 */
export interface Hit {
  doc: Doc;
  snippet: string;
}

export function search(docs: Doc[], query: string): Hit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  const hits: Hit[] = [];
  for (const doc of docs) {
    if (`${doc.title}\n${doc.summary}`.toLowerCase().includes(q)) {
      hits.push({ doc, snippet: doc.summary });
      continue;
    }
    const at = doc.body.toLowerCase().indexOf(q);
    if (at >= 0) {
      const from = Math.max(0, at - 40);
      const text = doc.body
        .slice(from, at + q.length + 60)
        .replace(/[#*`>_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      hits.push({ doc, snippet: `${from > 0 ? '…' : ''}${text}…` });
    }
  }
  return hits;
}
